import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { adminAPI } from '../api/client';
import { CURRENCY } from '../config/constants';
import { Loader, AlertCircle, Fuel, Droplet, Receipt, CalendarDays, X } from 'lucide-react';
import { formatIstDateTime, istToday } from '../utils/formatIst';
import { useGlobalSearch } from '../context/SearchContext';
import { matchesGlobalSearch } from '../utils/globalSearch';

const money = (v) => `${CURRENCY.SYMBOL}${(parseFloat(v) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
const liters = (v) => `${(parseFloat(v) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })} L`;

// Resolve a stored receipt value (base64, data URL, or http URL) to an <img> src.
const resolveReceiptSrc = (value) => {
  if (!value || typeof value !== 'string') return null;
  const v = value.trim();
  if (v.startsWith('data:')) return v;
  if (/^https?:\/\//i.test(v)) return v;
  // Long base64-looking blobs (no path separators) — assume JPEG.
  if (v.length > 100 && !v.includes('/') && /^[A-Za-z0-9+/=\s]+$/.test(v)) {
    return `data:image/jpeg;base64,${v.replace(/\s+/g, '')}`;
  }
  return null;
};

const SummaryCard = ({ icon: Icon, label, value, tint }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center gap-3">
    <div className={`p-3 rounded-xl ${tint}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div className="min-w-0">
      <p className="text-xs text-gray-500 uppercase font-semibold">{label}</p>
      <p className="text-lg font-bold text-gray-900 truncate">{value}</p>
    </div>
  </div>
);

const FuelExpenses = () => {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState({ total_cost: 0, total_liters: 0, entry_count: 0, mtd_cost: 0 });
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [receiptSrc, setReceiptSrc] = useState(null);
  const { query: globalSearch } = useGlobalSearch();

  const [filters, setFilters] = useState({ rep_id: '', from: '', to: '' });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.rep_id) params.rep_id = filters.rep_id;
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;
      const res = await adminAPI.getFuelLogs(params);
      setLogs(res.data.data || []);
      setSummary(res.data.summary || { total_cost: 0, total_liters: 0, entry_count: 0, mtd_cost: 0 });
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load fuel expenses');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    adminAPI.getAllReps().then((res) => setReps(res.data.data || [])).catch(() => {});
  }, []);

  const filteredLogs = useMemo(() => {
    return logs.filter((l) =>
      matchesGlobalSearch(globalSearch, [
        l.rep_name,
        l.total_amount,
        l.fuel_quantity_liters,
        l.odometer_reading,
        l.logged_at || l.created_at,
      ])
    );
  }, [logs, globalSearch]);

  const avgPerLiter = summary.total_liters > 0 ? summary.total_cost / summary.total_liters : 0;

  const setThisMonth = () => {
    const today = istToday();
    const first = `${today.slice(0, 8)}01`;
    setFilters((f) => ({ ...f, from: first, to: today }));
  };

  return (
    <div className="admin-page space-y-6">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Fuel Expenses</h2>
          <p className="text-gray-600">Fuel logs and spend summary across your team</p>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard icon={Fuel} label="Total Spend" value={money(summary.total_cost)} tint="bg-amber-50 text-amber-600" />
        <SummaryCard icon={Droplet} label="Total Fuel" value={liters(summary.total_liters)} tint="bg-blue-50 text-blue-600" />
        <SummaryCard icon={CalendarDays} label="This Month" value={money(summary.mtd_cost)} tint="bg-emerald-50 text-emerald-600" />
        <SummaryCard icon={Receipt} label="Entries" value={`${summary.entry_count}`} tint="bg-purple-50 text-purple-600" />
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Representative</label>
            <select
              value={filters.rep_id}
              onChange={(e) => setFilters({ ...filters, rep_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">All reps</option>
              {reps.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">From</label>
            <input
              type="date"
              value={filters.from}
              onChange={(e) => setFilters({ ...filters, from: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">To</label>
            <input
              type="date"
              value={filters.to}
              onChange={(e) => setFilters({ ...filters, to: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex gap-2">
            <button onClick={setThisMonth} className="px-3 py-2 text-sm font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100">
              This month
            </button>
            {(filters.rep_id || filters.from || filters.to) && (
              <button onClick={() => setFilters({ rep_id: '', from: '', to: '' })} className="px-3 py-2 text-sm font-semibold text-gray-600 bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200">
                Clear
              </button>
            )}
          </div>
        </div>
        {summary.total_liters > 0 && (
          <p className="text-xs text-gray-500 mt-3">
            Average cost: <strong>{money(avgPerLiter)}</strong> per litre
          </p>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 admin-table-shell">
        {loading ? (
          <div className="p-10 flex justify-center">
            <Loader className="w-7 h-7 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="admin-table-scroll">
            <table className="admin-data-table">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Rep</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Amount</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Litres</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Odometer</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Receipt</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredLogs.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-10 text-center text-gray-500">
                      {logs.length === 0 ? 'No fuel logs for the selected period.' : 'No entries match your search.'}
                    </td>
                  </tr>
                ) : (
                  filteredLogs.map((l) => {
                    const src = resolveReceiptSrc(l.receipt_photo_path);
                    return (
                      <tr key={l.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-semibold text-gray-900">{l.rep_name || `Rep #${l.rep_id}`}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {formatIstDateTime(l.logged_at || l.created_at)}
                        </td>
                        <td className="px-6 py-4 text-right font-semibold text-gray-900">{money(l.total_amount)}</td>
                        <td className="px-6 py-4 text-right text-gray-700">{liters(l.fuel_quantity_liters)}</td>
                        <td className="px-6 py-4 text-right text-gray-700">
                          {l.odometer_reading != null ? `${l.odometer_reading} km` : '—'}
                        </td>
                        <td className="px-6 py-4">
                          {src ? (
                            <button onClick={() => setReceiptSrc(src)} className="text-blue-600 hover:underline text-sm font-semibold flex items-center gap-1">
                              <Receipt className="w-4 h-4" /> View
                            </button>
                          ) : (
                            <span className="text-gray-400 text-sm">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {receiptSrc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setReceiptSrc(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-3 border-b border-gray-200">
              <span className="font-semibold text-gray-800">Fuel receipt</span>
              <button onClick={() => setReceiptSrc(null)} className="text-gray-400 hover:text-gray-700 p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-3 max-h-[75vh] overflow-auto">
              <img src={receiptSrc} alt="Fuel receipt" className="w-full rounded-lg" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FuelExpenses;
